/**
 * OnboardingStepIndicator
 * Step progress bar for the onboarding wizard.
 * Shows 6 circles connected by lines with completed/current/upcoming states.
 * Responsive: horizontal on desktop, vertical on mobile.
 */
import React from 'react';
import PropTypes from 'prop-types';

const STEPS = [
  { number: 1, label: 'Business Info' },
  { number: 2, label: 'Contact Details' },
  { number: 3, label: 'Service Areas' },
  { number: 4, label: 'Pricing' },
  { number: 5, label: 'Verification' },
  { number: 6, label: 'Review & Submit' },
];

export default function OnboardingStepIndicator({ currentStep, completedSteps, onStepClick }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      gap: isMobile ? '0' : '0',
      padding: '24px 16px',
      backgroundColor: '#fff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      marginBottom: '24px',
      overflowX: 'auto',
    }}>
      {STEPS.map((step, index) => {
        const isCompleted = completedSteps.includes(step.number);
        const isCurrent = currentStep === step.number;
        const isUpcoming = !isCompleted && !isCurrent;
        const isLast = index === STEPS.length - 1;

        return (
          <React.Fragment key={step.number}>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'row' : 'column',
                alignItems: 'center',
                gap: isMobile ? '12px' : '8px',
                cursor: onStepClick ? 'pointer' : 'default',
                minWidth: isMobile ? 'auto' : '100px',
                padding: isMobile ? '8px 0' : '0',
              }}
              onClick={() => onStepClick && onStepClick(step.number)}
              role="button"
              tabIndex={0}
              aria-label={`Step ${step.number}: ${step.label}${isCompleted ? ' (completed)' : isCurrent ? ' (current)' : ''}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick && onStepClick(step.number);
                }
              }}
            >
              {/* Circle */}
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0,
                transition: 'all 0.2s ease',
                backgroundColor: isCompleted
                  ? '#22c55e'
                  : isCurrent
                    ? '#3b82f6'
                    : '#f1f5f9',
                color: isCompleted || isCurrent ? '#fff' : '#94a3b8',
                border: isCurrent
                  ? '3px solid #93c5fd'
                  : isCompleted
                    ? '3px solid #86efac'
                    : '2px solid #e2e8f0',
                boxShadow: isCurrent
                  ? '0 0 0 4px rgba(59, 130, 246, 0.15)'
                  : 'none',
                animation: isCurrent ? 'pulse-step 2s ease-in-out infinite' : 'none',
              }}>
                {isCompleted ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5L6.5 12L13 4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  step.number
                )}
              </div>

              {/* Label */}
              <span style={{
                fontSize: '12px',
                fontWeight: isCurrent ? '700' : '500',
                color: isCompleted
                  ? '#166534'
                  : isCurrent
                    ? '#1e40af'
                    : '#94a3b8',
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}>
                {step.label}
              </span>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div style={{
                flex: isMobile ? 'none' : '1',
                height: isMobile ? '20px' : '2px',
                width: isMobile ? '2px' : 'auto',
                minWidth: isMobile ? '2px' : '24px',
                marginLeft: isMobile ? '17px' : '0',
                backgroundColor: isCompleted ? '#86efac' : '#e2e8f0',
                borderRadius: '1px',
                transition: 'background-color 0.2s ease',
              }} />
            )}
          </React.Fragment>
        );
      })}

      {/* Pulse animation keyframes injected via style tag */}
      <style>{`
        @keyframes pulse-step {
          0%, 100% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15); }
          50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.08); }
        }
      `}</style>
    </div>
  );
}

OnboardingStepIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
  completedSteps: PropTypes.arrayOf(PropTypes.number).isRequired,
  onStepClick: PropTypes.func,
};
